import { Link } from 'react-router-dom';
import { BookOpen, Beaker, Sparkles, Users } from 'lucide-react';
import type { Track } from '../hooks/useApi';

// Audience hints for each track
const audienceHints: Record<string, string> = {
  'ai-rock-star': 'Best for: anyone starting from scratch with AI',
  'thinking-skills': 'Best for: people tired of feeling overwhelmed by information',
  'mindful-machine': 'Best for: folks who feel fried by the last few years',
  'ai-literacy': 'Best for: teachers, parents, and civic-minded adults',
  'small-products-workers': 'Best for: tinkerers, solo hackers, and indie makers',
  'nd-superpowers': 'Best for: neurodivergent folks who want AI as scaffolding',
  'cognitive-biases': 'Best for: anyone wanting to think more clearly',
};

interface TrackCardProps {
  track: Track;
  progress?: number;
}

export default function TrackCard({ track, progress }: TrackCardProps) {
  const levelLabel = track.level === 1 ? 'Core' : track.level === 2 ? 'Lab' : 'Advanced';
  const levelColor = track.level === 1
    ? 'text-accent-blue border-accent-blue/30'
    : track.level === 2
    ? 'text-accent-purple border-accent-purple/30'
    : 'text-accent-orange border-accent-orange/30';

  const audienceHint = audienceHints[track.slug];

  const Icon = track.level === 1 ? BookOpen : track.level === 2 ? Beaker : Sparkles;

  return (
    <Link
      to={`/tracks/${track.slug}`}
      className="group block bg-background-secondary rounded-xl p-5 border border-gray-800 hover:border-gray-600 card-lift"
    >
      <div className="flex items-start gap-4">
        <div className={`p-2 rounded-lg bg-gray-800/50 ${levelColor}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${levelColor}`}>
            {levelLabel} Track
          </div>
          <h3 className="font-semibold text-white group-hover:text-accent-blue transition-colors line-clamp-2">
            {track.title}
          </h3>
          <p className="text-sm text-gray-400 mt-2 line-clamp-2">
            {track.description}
          </p>

          {audienceHint && (
            <div className="flex items-center gap-1.5 mt-2">
              <Users className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs text-gray-400 italic">{audienceHint}</span>
            </div>
          )}

          {progress !== undefined && progress > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>Progress</span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent-green rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
